// Types, imported from their own modules rather than the feature barrel, and as `import type`.
//
// Both are used only as types, but a value import of the barrel is not elided, so loading this module
// pulled the whole BuildingProject index — and through it the Apollo client, which has no WebSocket to
// construct outside a browser. That is what made these rules untestable.
import type { TransportTypesType } from "../types/TransportTypesType";
import type { WizardProjectType } from "../types/WizardProjectType";
import { Transport_Type_Enum } from "@/graphql";
import { validateCountry } from "@/providers/Localization";

// ///////////////////
// Helper functions
// ///////////////////

const isRequired = (value: any) => value !== null && value !== undefined && value.length > 0;
const isValidNumber = (value: number | null, minValue: number = 0) => value !== null && value >= minValue;
const isNonNegative = (value: number | null) => value !== null && value >= 0;
const doesNotExceed = (value: number | null, max: number | null) => value === null || max === null || value <= max;

// ///////////////////
// Validation functions
// ///////////////////

export const validateAddress = (project: WizardProjectType) => {
  // Data
  const {
    buildingAddress: { street, streetNumber, city, postalCode, country },
  } = project;

  return [
    // Address
    isRequired(street),
    isRequired(streetNumber),
    isRequired(city),
    isRequired(postalCode),
    validateCountry(country),
  ].every((value) => value);
};

/**
 * What the CREATE flow needs from the Location step: a country, and nothing else.
 *
 * The street address is real project data — it goes to the API on create and persists as a location row
 * — but it is not needed to get started, and demanding it was making people invent one. The step's own
 * hint used to say "if there is no valid address yet, use a nearby one", which is an admission that the
 * requirement was being worked around rather than met.
 *
 * The country still has to be known, because it decides which steps the flow has and which emission data
 * is loaded. It is: the start-project dialog writes it onto the address before the wizard opens, so this
 * passes on an otherwise empty address.
 *
 * Separate from validateAddress rather than a change to it. That was written when the EDIT wizard shared
 * this step and demanded the whole address; since RTLCA-5812 the settings page's Location section is
 * optional too (see its `valid` in settingsSections.ts), so validateAddress currently has no caller —
 * kept because "a complete address" is still the rule any host that wants one should reach for.
 */
export const validateCountryKnown = (project: WizardProjectType) => {
  const {
    buildingAddress: { country },
  } = project;

  return validateCountry(country);
};

export const validateProjectDetails = (project: WizardProjectType) => {
  // Data
  const {
    projectName,
    buildingProjectStageEnum,
    emissionCalculationAssumptions: { emissionMetaData },
  } = project;

  return [
    // Projet details
    isRequired(projectName),
    isNonNegative(buildingProjectStageEnum),
    isValidNumber(emissionMetaData.yearOfCommissioning, 2023),
    isValidNumber(emissionMetaData.yearOfConstruction, 2023),
  ].every((value) => value);
};

// Building type moved onto this step's own row (RTLCA-5661 follow-up): its limit-value dependency is
// with the areas below, not with the project's name/status/years, so it validates here now.
export const validateBuildingDetails = (project: WizardProjectType) => {
  // Data
  const {
    buildingTypeEnum,
    emissionCalculationAssumptions: { referencedAreas, heatArea },
  } = project;

  // Below ground is optional (RTLCA-5720): empty or 0 means no basement, and a building with no
  // basement has no basement floors or heated basement area to ask for. Every reader already treats
  // a missing below-ground area as 0 (`sumOrNull`, the `|| 0` totals, B6's `?? 0`).
  const hasBasement = (referencedAreas.areaBelowGround ?? 0) > 0;

  return [
    isNonNegative(buildingTypeEnum),
    // Building details
    isNonNegative(referencedAreas.areaAboveGround),
    isNonNegative(referencedAreas.floorsAboveGround),
    isNonNegative(heatArea.heatedFloorAreaAboveGround),
    referencedAreas.areaBelowGround === null || isNonNegative(referencedAreas.areaBelowGround),
    !hasBasement || isNonNegative(referencedAreas.basementFloors),
    !hasBasement || isNonNegative(heatArea.heatedBasementArea),
  ].every((value) => value);
};

/**
 * What the CREATE flow's Building step needs beyond validateBuildingDetails: heated area can never
 * exceed its group's gross area. Confirmed against the backend (realtimelca-api): gross areas are total
 * floor areas summed as-is into the per-m2 denominator, floor counts are stored-only and never multiply
 * into that math, and heated areas are likewise plain sums with no cross-field validation anywhere. A
 * heated value bigger than its own group's gross area is not measuring anything real, per group:
 * heatedFloorAreaAboveGround <= areaAboveGround, heatedBasementArea <= areaBelowGround.
 *
 * Kept separate from validateBuildingDetails rather than folded into it, because the EDIT wizard calls
 * that shared function directly for its own Building step (settingsSections.ts), against stored data that
 * predates this rule. Folding the relational checks into the shared function would start blocking saves
 * on existing projects whose heated area already exceeds their gross area, for a value the edit screen
 * never asked the user to type.
 */
export const validateBuildingDetailsForCreate = (project: WizardProjectType) => {
  const {
    emissionCalculationAssumptions: { referencedAreas, heatArea },
  } = project;

  return [
    validateBuildingDetails(project),
    doesNotExceed(heatArea.heatedFloorAreaAboveGround, referencedAreas.areaAboveGround),
    doesNotExceed(heatArea.heatedBasementArea, referencedAreas.areaBelowGround),
  ].every((value) => value);
};

/** Each row of a mixed-use table: its own type, an area above ground above 0, and floors and heated area
 * wherever that side has an area, the heated area never above it. A single use is the project-level fields. */
export const validateBuildingUses = (project: WizardProjectType) => {
  const uses = project.buildingUses ?? [];
  if (uses.length < 2) return true;

  const typeIds = uses.map((use) => use.buildingTypeId);
  const sideValid = (area: number | null, floors: number | null, heated: number | null) =>
    !((area ?? 0) > 0) || (isNonNegative(floors) && isNonNegative(heated) && doesNotExceed(heated, area));

  return (
    typeIds.every((typeId) => typeId !== null && typeId !== undefined) &&
    new Set(typeIds.map(Number)).size === typeIds.length &&
    uses.every(
      (use) =>
        (use.areaAboveGround ?? 0) > 0 &&
        sideValid(use.areaAboveGround ?? null, use.floorsAboveGround ?? null, use.heatedAreaAboveGround ?? null) &&
        sideValid(use.areaBelowGround ?? null, use.floorsBelowGround ?? null, use.heatedAreaBelowGround ?? null)
    )
  );
};

const heatedAreaGroups = ["aboveGround", "belowGround"] as const;
export type HeatedAreaGroup = (typeof heatedAreaGroups)[number];

const heatedPairs = (candidate: WizardProjectType) => {
  const { referencedAreas, heatArea } = candidate.emissionCalculationAssumptions;
  return {
    aboveGround: { heated: heatArea.heatedFloorAreaAboveGround, gross: referencedAreas.areaAboveGround },
    belowGround: { heated: heatArea.heatedBasementArea, gross: referencedAreas.areaBelowGround },
  };
};

/** Whether a group's heated or gross value differs from `saved`; no baseline reads as unedited. */
export const isHeatedAreaGroupEdited = (group: HeatedAreaGroup, project: WizardProjectType, saved?: WizardProjectType | null) => {
  if (!saved) return false;
  const live = heatedPairs(project)[group];
  const stored = heatedPairs(saved)[group];
  return live.heated !== stored.heated || live.gross !== stored.gross;
};

/** The EDIT flow's heated-area rule: enforced only on a pair changed since `saved`, so a stored project
 * that already breaks it stays saveable until someone touches that pair. No baseline enforces nothing. */
export const validateHeatedAreasForEdit = (project: WizardProjectType, saved?: WizardProjectType | null) =>
  heatedAreaGroups.every((group) => {
    const { heated, gross } = heatedPairs(project)[group];
    return !isHeatedAreaGroupEdited(group, project, saved) || doesNotExceed(heated, gross);
  });

export const validateCalculationPrerequisites = (project: WizardProjectType) => {
  const {
    emissionCalculationAssumptions: { calculationType, emissionMetaData },
  } = project;

  return [
    isNonNegative(calculationType.calculationTypeEnum),
    isNonNegative(calculationType.considerationPeriod),
    isNonNegative(calculationType.thresholdLimitWithoutExtra),
    isNonNegative(emissionMetaData.includedPhases.length),
  ].every((value) => value);
};

/**
 * What the CREATE flow's Calculation step needs beyond validateCalculationPrerequisites: for Custom, at
 * least one life-cycle stage chosen. Same split `validateBuildingDetailsForCreate` draws from
 * `validateBuildingDetails` — the EDIT wizard's own Calculation step still calls the shared
 * `validateCalculationPrerequisites` directly, unchanged.
 *
 * `isCustomCalculationType` arrives resolved rather than looked up from `calculationTypeEnum`, so this
 * file stays a plain function of the project, callable from `valid()` with no store dependency, the same
 * reason validateTransport takes its transport rows as a second argument instead of a store lookup. The
 * caller already has to resolve it to gate LifecycleStageStrip's `editable` prop, so nothing here repeats
 * that lookup, it reuses the caller's answer.
 *
 * The stages check is the one rule this changes rather than carries over: validateCalculationPrerequisites'
 * `isNonNegative(includedPhases.length)` is true for an empty array just as much as a full one, so it never
 * actually blocked Custom with nothing chosen. That block came from CalculationPrerequisites.vue's own
 * banner, keyed on `calculationTypeEnum === 0`, which only matches a country whose Custom row happens to be
 * id 0 (Sweden's is 20). The create path gets the real rule: keyed on being Custom, not on which id Custom
 * happens to be.
 */
export const validateCalculationPrerequisitesForCreate = (project: WizardProjectType, isCustomCalculationType: boolean) => {
  const {
    emissionCalculationAssumptions: { emissionMetaData },
  } = project;

  const hasChosenStages = (emissionMetaData.includedPhasesTranslated?.length ?? 0) > 0;

  return [validateCalculationPrerequisites(project), !isCustomCalculationType || hasChosenStages].every((value) => value);
};

export const validateReferenceArea = (project: WizardProjectType) => {
  const {
    emissionCalculationAssumptions: {
      referencedAreas: { values, entries },
    },
  } = project;

  if (entries && entries.length > 0) {
    // A type-less entry is a row whose structure select is still on "Select" — kept in state so it
    // survives navigation, but it must block the step, or the payload strip would discard it silently.
    return entries.every((entry) => entry.type !== "" && isNonNegative(entry.area));
  }

  return Object.values(values).every((value) => isNonNegative(value));
};

export const validateInstallationEnergyUse = (project: WizardProjectType): boolean => {
  const {
    emissionCalculationAssumptions: {
      projectEnergyData: { constructional },
    },
  } = project;

  const earthMoved = constructional?.earthMoved || {
    sourceId: null,
    type: "",
    value: null,
  };
  const electricity = constructional?.electricity || {
    sourceId: null,
    type: "",
    value: null,
  };
  const heat = constructional?.heat || { sourceId: null, type: "", value: null };
  const machineryFuel = constructional?.machineryFuel || {
    sourceId: null,
    type: "",
    value: null,
  };

  return [earthMoved, electricity, heat, machineryFuel]
    .filter((data) => data.sourceId)
    .every((data) => isValidNumber(data.value, -999999999999));
};

export const validateOperationalEnergyUse = (project: WizardProjectType): boolean => {
  const {
    emissionCalculationAssumptions: {
      projectEnergyData,
      projectEnergyData: { operational },
    },
  } = project;

  const electricity = operational?.electricity || {
    sourceId: null,
    type: "",
    value: null,
  };
  const heat = operational?.heat || { sourceId: null, type: "", value: null };
  const exported = projectEnergyData.operationExportedElectricity;

  const isValidNumber = (value: unknown, min = -Infinity): boolean => typeof value === "number" && !isNaN(value) && value >= min;

  const isRequired = (val: unknown): boolean => (typeof val === "string" ? val.trim().length > 0 : val != null);

  const hasExportedElectricityInput = typeof exported === "number" && exported > 0;

  // Consider a sourceId being selected (even without value/type) as user input, so partial input is caught
  const hasUserInput = !!heat.sourceId || !!electricity.sourceId || hasExportedElectricityInput;

  const validatedBy =
    typeof projectEnergyData.validatedBy === "string" && projectEnergyData.validatedBy.trim().length > 0
      ? projectEnergyData.validatedBy.trim()
      : null;

  const validationDate =
    typeof projectEnergyData.validationDate === "string" && projectEnergyData.validationDate.trim().length > 0
      ? projectEnergyData.validationDate.trim()
      : null;

  if (!hasUserInput) return true;

  return (
    [heat, electricity].filter((data) => data.sourceId).every((data) => isRequired(data.type) && isValidNumber(data.value)) &&
    isRequired(validatedBy) &&
    isRequired(validationDate)
  );
};

export const validateTransport = (_: WizardProjectType, transportTypes?: TransportTypesType[]): boolean => {
  // All transportTypes should have label, distance, type and emission
  if (
    transportTypes?.find(
      (transport) =>
        transport.transport_type_name === null ||
        !transport.transport_type_name ||
        (transport.transport_type_name && transport.transport_type_name.length <= 0)
    )
  )
    return false;
  if (transportTypes?.find((transport) => transport?.distance === null || transport.distance <= 0)) return false;
  if (
    transportTypes?.find(
      (transport) => transport?.transport.transportType === null || transport?.transport.transportType.length <= -1
    )
  )
    return false;
  if (
    transportTypes?.find((transport) =>
      transport?.transport.transportType === Transport_Type_Enum.Custom
        ? transport?.emission === null || transport.emission <= 0
        : false
    )
  )
    return false;
  return true;
};
